import React, { useState, useRef, useEffect } from 'react';
import {
  Popup, Menu, Progress, Icon, Grid, Button, Input,
} from 'semantic-ui-react';
import api from 'api';
import util from 'utils/utils';
import { withTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import useTheme from 'theme/useTheme';

import OneDriveFileChooser from './OneDriveFileChooser';

const ImageChooserPopup = styled(Popup)`
z-index:5;
max-width:400px !important;
.menu{
  max-width:400px !important;
  min-width:300px !important;
}
.unsplash-thumb{
  width:100%;
  height:100%;
  background-size: cover;
  background-position: center center;
  cursor:pointer;
  display:inline-block;
  p{
    display:none;
    font-size:9px;
    position:absolute;
    top:0px;
    left:0px;
    margin:2px;
  }
  .unsplash-fader{
    position:absolute;
    top:0px;
    left:0px;
    width:100%;
    height:100%;
    opacity:0.0;
    background:rgba(250,250,250, 0.6);
    transition:opacity 0.25s;
  }
  &:hover{
    .unsplash-fader{
      opacity:0.8;
    }
    p{
      display: block;
    }
  }
}
.unsplash-controls{
  text-align:right;
  margin:6px;
}
`;

function ImageChooser({
  user, forType, forId, isOpen, onOpen, onClose, onComplete, onError, trigger, position, content, t, isBasic, zIndex,
}) {
  const [oneDriveOpen, setOneDriveOpen] = useState(false);
  const [unsplashTerm, setUnsplashTerm] = useState('');
  const [unsplashImages, setUnsplashImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [maximumPage, setMaximumPage] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [noResults, setNoResults] = useState(false);
  const [open, setOpen] = useState(isOpen);
  const fileInput = useRef(null);
  const theme = useTheme();

  const clientId = import.meta.env.VITE_UNSPLASH_CLIENT_ID;

  useEffect(() => {
    if (isOpen === undefined) {
      return;
    }
    if (isOpen !== open) {
      setOpen(isOpen);
    }
  }, [isOpen, open]);

  // Button handlers for manually opening and closing the chooser popup
  const closePopup = () => {
    if (open && onClose) onClose();
    setOpen(false);
  };

  const openPopup = () => {
    if (open) return closePopup();
    if (!open && onOpen) onOpen();
    setOpen(true);
  };

  const onChosen = (responseFilename, filename, responseDownload) => {
    setOneDriveOpen(false);
    onComplete(responseFilename, filename, responseDownload);
    closePopup();
  };

  const chooseFile = () => fileInput.current.click();
  const uploadFile = (file) => {
    if (file) {
      setIsUploading(true);
      setUploadProgress(1);
      const regex = new RegExp('[^a-zA-Z0-9_.]', 'g');
      const name = file.name.replace(regex, '-');
      api.uploads.generateFileUploadRequest({
        forType, forId, name, size: file.size, type: file.type,
      }, (response) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', response.signedRequest);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(parseInt((e.loaded * 100) / e.total, 10));
          }
        });
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            setIsUploading(false);
            setUploadProgress(100);
            if (xhr.status === 200) {
              onChosen(response.fileName, name, response.signedDownloadRequest);
            } else if (onError) {
              onError('Unable to upload file');
            }
          }
        };
        xhr.send(file);
      }, (err) => {
        setIsUploading(false);
        setUploadProgress(0);
        if (onError) onError(err.message || 'Unable to upload file');
        else toast.error(err.message);
      });
    }
  };
  const handleFileChosen = (e) => {
    uploadFile(e.target.files && e.target.files[0]);
  };

  const chooseFromUnsplash = (image) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${image.links.download_location}&client_id=${clientId}`);
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          onChosen(`unsplash-${data.url}`, null, data.url);
        }
      }
    };
    xhr.send();
  };

  const queryUnsplash = (term, page) => {
    if (!term) return;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `https://api.unsplash.com/search/photos/?query=${term}&page=${page}&per_page=9&client_id=${clientId}`);
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          setUnsplashImages(data.results);
          setMaximumPage(data.total_pages);
          setNoResults(data.results.length === 0);
        }
      }
    };
    xhr.send();
  };

  const searchUnsplash = () => {
    queryUnsplash(unsplashTerm, 1);
    setCurrentPage(1);
  };

  const prevUnsplashPage = (e) => {
    e.stopPropagation();
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      queryUnsplash(unsplashTerm, newPage);
    }
  };

  const nextUnsplashPage = (e) => {
    e.stopPropagation();
    if (currentPage < maximumPage) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      queryUnsplash(unsplashTerm, newPage);
    }
  };

  const handleKeyDown = (e) => {
    e.stopPropagation();
    if (e.keyCode === 13) {
      e.preventDefault();
      searchUnsplash(e);
    }
  };

  return (
    <>
      <OneDriveFileChooser
        user={user}
        forType={forType}
        forId={forId}
        imagesOnly
        open={oneDriveOpen}
        onComplete={onChosen}
        onError={onError}
        onClose={() => setOneDriveOpen(false)}
      />
      <ImageChooserPopup on='click' onClose={() => closePopup()} position={position || (theme.sizes.isMobile ? 'bottom center' : 'bottom left')} open={open} style={{ zIndex: zIndex || 5 }}
        trigger={trigger
          ? React.cloneElement(trigger, {
            loading: isUploading,
            onClick: openPopup,
          })
          : <Button size='small' secondary basic={isBasic} icon='image' content={content || t('ideas.details.chooseCover')} loading={isUploading} onClick={openPopup} />
        }
        content={
          <div>
            <h5 style={{ margin: 0 }}>Upload an image</h5>
            <Menu vertical borderless style={{ marginTop: 10 }}>
              {isUploading && <Progress percent={uploadProgress} indicating progress style={{ margin: 5 }} />}
              <Menu.Item onClick={chooseFile}><i className="upload icon"></i>{t('ideas.actions.chooseImage')}</Menu.Item>
              <input type='file' style={{ display: 'none' }} ref={fileInput} onChange={handleFileChosen} accept='image/*' />
              {util.userIsConnectedTo(user, 'office365') ? (
                <Menu.Item onClick={() => setOneDriveOpen(!oneDriveOpen)} icon='microsoft' content='Choose from OneDrive' />
              ) : null}
            </Menu>
            <h5 style={{ marginTop: 0, marginBottom: 0 }}>Search online for an image</h5>
            <p style={{ margin: 0 }}><small>Courtesy of <a href="https://unsplash.com/?utm_source=simply_do&utm_medium=referral" target="_blank" rel="noopener noreferrer">Unsplash</a></small></p>
            <Input fluid icon='search' iconPosition='left' placeholder='Search for an image...' onClick={(e) => e.stopPropagation()} style={{ marginBottom: 20 }}
              onKeyDown={(e) => handleKeyDown(e)}
              onChange={(e) => setUnsplashTerm(e.target.value)}
              action={<Button primary content='Go' onClick={searchUnsplash} />}
            />
            {noResults
              && <Grid.Column textAlign='center'>
                <span>No images found.</span>
              </Grid.Column>
            }
            <div style={{ textAlign: 'center' }}>
              {unsplashImages.map((image) => <div style={{
                height: 100, width: 100, position: 'relative', display: 'inline-block', margin: 0,
              }} key={image.urls.thumb}>
                <div className='unsplash-thumb' style={{ backgroundImage: `url(${image.urls.thumb})` }} onClick={() => chooseFromUnsplash(image)}>
                  <div className='unsplash-fader' />
                  <p>Photo by <a href={`https://unsplash.com/@${image.user.username}?utm_source=simply_do&utm_medium=referral`} target='_blank' rel="noopener noreferrer">{image.user.name}</a></p>
                </div>
              </div>)}
            </div>

            <div className='unsplash-controls'>
              {currentPage > 1
                && <Button basic size='tiny' onClick={prevUnsplashPage}>
                  <Icon name='arrow left' /> Previous page
                </Button>
              }
              {currentPage < maximumPage
                && <Button basic size='tiny' onClick={nextUnsplashPage}>
                  Next page <Icon name='arrow right' />
                </Button>
              }
            </div>
            <div style={{ textAlign: 'left', marginTop: 10 }}>
              <Button basic size='tiny' content={t('generic.cancel')} onClick={closePopup} />
            </div>
          </div>
        } />
      </>
  );
}

export default withTranslation()(ImageChooser);
